import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function Pagination({
  page = 1,
  pageSize = 25,
  total = 0,
  totalPages,
  onPageChange,
}) {
  // ---------------------------------------------------------
  // Server-side pagination
  // ---------------------------------------------------------
  const calculatedTotalPages = Math.ceil(
    total / pageSize
  );

  const pagesCount =
    typeof totalPages === "number"
      ? totalPages
      : calculatedTotalPages;

  // No pagination needed
  if (total <= pageSize || pagesCount <= 1) {
    return null;
  }

  const currentPage = Math.min(
    Math.max(page, 1),
    pagesCount
  );

  const start = (currentPage - 1) * pageSize + 1;

  const end = Math.min(
    currentPage * pageSize,
    total
  );

  // ---------------------------------------------------------
  // Generate page numbers
  // ---------------------------------------------------------

  const getPages = () => {
    const pages = [];

    if (pagesCount <= 7) {
      for (let i = 1; i <= pagesCount; i++) {
        pages.push(i);
      }

      return pages;
    }

    pages.push(1);

    if (currentPage > 4) {
      pages.push("left-ellipsis");
    }

    const startPage = Math.max(
      2,
      currentPage - 1
    );

    const endPage = Math.min(
      pagesCount - 1,
      currentPage + 1
    );

    for (
      let i = startPage;
      i <= endPage;
      i++
    ) {
      pages.push(i);
    }

    if (currentPage < pagesCount - 3) {
      pages.push("right-ellipsis");
    }

    pages.push(pagesCount);

    return pages;
  };

  // ---------------------------------------------------------
  // Page change
  // ---------------------------------------------------------

  const handlePageChange = (nextPage) => {
    if (
      nextPage < 1 ||
      nextPage > pagesCount ||
      nextPage === currentPage
    ) {
      return;
    }

    onPageChange(nextPage);
  };

  return (
    <div className="flex items-center justify-between gap-4 border-t border-border bg-white px-4 py-3">
      {/* -------------------------------------------------- */}
      {/* Result count */}
      {/* -------------------------------------------------- */}

      <div className="text-xs text-muted-foreground whitespace-nowrap">
        Showing{" "}
        <span className="font-semibold text-foreground">
          {start}
        </span>{" "}
        to{" "}
        <span className="font-semibold text-foreground">
          {end}
        </span>{" "}
        of{" "}
        <span className="font-semibold text-foreground">
          {total}
        </span>
      </div>

      {/* -------------------------------------------------- */}
      {/* Pagination controls */}
      {/* -------------------------------------------------- */}

      <div className="flex items-center justify-center gap-1">
        {/* Previous */}
        <button
          type="button"
          onClick={() =>
            handlePageChange(currentPage - 1)
          }
          disabled={currentPage === 1}
          className="grid h-8 w-8 place-items-center rounded-lg border border-border bg-white text-foreground transition hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Previous page"
        >
          <ChevronLeft size={16} />
        </button>

        {/* Page numbers */}
        {getPages().map((item, index) => {
          if (
            item === "left-ellipsis" ||
            item === "right-ellipsis"
          ) {
            return (
              <span
                key={`${item}-${index}`}
                className="grid h-8 w-8 place-items-center text-xs text-muted-foreground"
              >
                ...
              </span>
            );
          }

          return (
            <button
              key={item}
              type="button"
              onClick={() =>
                handlePageChange(item)
              }
              className={
                currentPage === item
                  ? "grid h-8 min-w-8 place-items-center rounded-lg bg-primary px-2 text-xs font-semibold text-white"
                  : "grid h-8 min-w-8 place-items-center rounded-lg border border-border bg-white px-2 text-xs font-semibold text-foreground hover:bg-secondary"
              }
            >
              {item}
            </button>
          );
        })}

        {/* Next */}
        <button
          type="button"
          onClick={() =>
            handlePageChange(currentPage + 1)
          }
          disabled={
            currentPage === pagesCount
          }
          className="grid h-8 w-8 place-items-center rounded-lg border border-border bg-white text-foreground transition hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Next page"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}